// src/components/manager/shared/PostComposer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Plus, X, Image, Paperclip, Globe, Lock, Users, LinkIcon, ExternalLink,
  Send, Calendar, ChevronDown, Tag, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';
import { parseVideoUrl, getPlatformIcon } from '../../../utils/mediaUtils';

export const PostComposer = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialPost = null,
  templateType = 'organisation',
  bookId,
  user
}) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [category, setCategory] = useState('announcement');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [media, setMedia] = useState([]);
  const [tags, setTags] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [priority, setPriority] = useState('normal');
  
  const [externalVideoUrl, setExternalVideoUrl] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [videoUrlError, setVideoUrlError] = useState('');
  // ✅ Use the standard uploadStates pattern
  const [uploadStates, setUploadStates] = useState({});
  
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const modalRef = useRef(null);

  const visibilityOptions = {
    public: { icon: Globe, label: 'Public', desc: 'Everyone can see this', color: 'text-green-600' },
    internal: { icon: Lock, label: 'Internal', desc: 'Members only', color: 'text-blue-600' },
    private: { icon: Users, label: 'Private', desc: 'Only you', color: 'text-gray-600' }
  };

  const categoryOptions = {
    announcement: { emoji: '📢', label: 'Announcement' },
    news: { emoji: '📰', label: 'News' },
    update: { emoji: '🔄', label: 'Update' },
    event: { emoji: '📅', label: 'Event' },
    achievement: { emoji: '🏆', label: 'Achievement' }
  };

  const priorityOptions = {
    low: { color: 'bg-gray-100 text-gray-700', label: 'Low Priority' },
    normal: { color: 'bg-blue-100 text-blue-700', label: 'Normal' },
    high: { color: 'bg-red-100 text-red-700', label: 'High Priority' }
  };

  // Load initialPost data
  useEffect(() => {
    if (initialPost) {
      setTitle(initialPost.title || '');
      setContent(initialPost.content || '');
      setVisibility(initialPost.visibility || 'public');
      setCategory(initialPost.category || 'announcement');
      setMedia(initialPost.media || []);
      setTags(Array.isArray(initialPost.tags) ? initialPost.tags.join(', ') : (initialPost.tags || ''));
      setScheduledDate(initialPost.scheduledDate || '');
      setPriority(initialPost.priority || 'normal');
    } else {
      setTitle('');
      setContent('');
      setVisibility('public');
      setCategory('announcement');
      setMedia([]);
      setTags('');
      setScheduledDate('');
      setPriority('normal');
    }
  }, [initialPost, isOpen]);

  useEffect(() => {
    if (isOpen && titleRef.current) {
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setTitle('');
    setContent('');
    setVisibility('public');
    setCategory('announcement');
    setMedia([]);
    setTags('');
    setScheduledDate('');
    setPriority('normal');
    setUploadStates({});
    onClose();
  };

  const handleSave = async () => {
    if (!content.trim() && !title.trim()) return;

    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);

    const postData = {
      title: title.trim() || content.substring(0, 50) + (content.length > 50 ? '...' : ''),
      content: content.trim(),
      visibility,
      category,
      media,
      tags: tagsArray,
      scheduledDate: scheduledDate || '',
      priority,
      status: 'approved',
      authorId: user?.uid || 'unknown',
      authorName: user?.displayName || user?.email || 'Unknown User',
      authorPhoto: user?.photoURL || '',
      bookId: bookId,
      views: 0,
      likes: 0,
      comments: 0,
      links: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await onSave(postData);
    handleClose();
  };

  // ✅ FIXED: Follow the standard upload pattern
  const handleMediaUpload = async (files, error) => {
    if (error) {
      setUploadStates(prev => ({ ...prev, media: { uploading: false, error } }));
      return;
    }

    if (!files || files.length === 0) return;

    const fileArray = Array.isArray(files) ? files : [files];
    setUploadStates(prev => ({ 
      ...prev, 
      media: { uploading: true, current: 0, total: fileArray.length } 
    }));

    try {
      const uploadedMedia = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        
        // Update progress
        setUploadStates(prev => ({ 
          ...prev, 
          media: { ...prev.media, current: i + 1 } 
        }));

        try {
          // Upload file and get URL string
          const urlString = await firestoreService.uploadFile(file, `pb-${bookId}/posts/media`);

          // Create media item matching Firestore structure
          const mediaItem = {
            url: urlString,
            fileName: file.name,
            fileSize: file.size,
            type: file.type.startsWith('image/') ? 'image' : 
                  file.type.startsWith('video/') ? 'video' : 
                  file.type.startsWith('audio/') ? 'audio' : 'document',
            uploadedAt: new Date().toISOString(),
            id: Date.now() + i
          };

          uploadedMedia.push(urlString);
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
        }
      }

      if (uploadedMedia.length > 0) {
        setMedia(prev => [...prev, ...uploadedMedia]);
        setUploadStates(prev => ({ ...prev, media: { uploading: false, success: true } }));
        
        // Clear success state after 2 seconds
        setTimeout(() => {
          setUploadStates(prev => ({ ...prev, media: {} }));
        }, 2000);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadStates(prev => ({ 
        ...prev, 
        media: { uploading: false, error: 'Failed to upload files' } 
      }));
    }
  };

  const removeMediaItem = (index) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExternalVideo = () => {
    if (!externalVideoUrl.trim()) {
      setVideoUrlError('Please enter a video URL');
      return;
    }

    const parsedVideo = parseVideoUrl(externalVideoUrl.trim());
    
    if (!parsedVideo) {
      setVideoUrlError('Unsupported video URL. Please use YouTube, Loom, Vimeo, or Google Drive links.');
      return;
    }

    // Add to media array with type 'external-video'
    const externalVideoItem = {
      url: parsedVideo.embedUrl,
      originalUrl: parsedVideo.originalUrl,
      fileName: `${parsedVideo.platform} Video`,
      fileSize: 0,
      type: 'external-video',
      platform: parsedVideo.platform,
      videoId: parsedVideo.videoId,
      thumbnailUrl: parsedVideo.thumbnailUrl,
      uploadedAt: new Date().toISOString(),
      id: Date.now()
    };

    setMedia(prev => [...prev, externalVideoItem]);
    setExternalVideoUrl('');
    setShowVideoInput(false);
    setVideoUrlError('');
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  const VisibilityIcon = visibilityOptions[visibility].icon;
  const isUploading = uploadStates.media?.uploading;
  const uploadError = uploadStates.media?.error;
  const uploadSuccess = uploadStates.media?.success;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">
              {initialPost ? 'Edit Post' : 'Create Post'}
            </h2>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                showAdvanced ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {showAdvanced ? 'Basic' : 'Advanced'}
            </button>
          </div>
          
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title */}
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title (optional)"
            className="w-full text-2xl font-bold border-none outline-none resize-none placeholder-gray-400"
          />

          {/* Main Content */}
          <textarea
            ref={contentRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What would you like to share?"
            rows={8}
            className="w-full border-none outline-none resize-none text-gray-700 placeholder-gray-400"
          />

          {/* Quick Settings */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 border-t border-gray-100">
            {/* Visibility Selector */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
              <div className="flex gap-2">
                {Object.entries(visibilityOptions).map(([key, option]) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setVisibility(key)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        visibility === key
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category Selector */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(categoryOptions).map(([key, option]) => (
                  <option key={key} value={key}>
                    {option.emoji} {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Media Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Media {media.length > 0 && `(${media.length})`}
            </label>
            
            {/* Upload Progress */}
            {isUploading && (
              <div className="mb-3 border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-700 font-medium">
                    Uploading {uploadStates.media.current} of {uploadStates.media.total}...
                  </span>
                </div>
                <div className="mt-3 w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(uploadStates.media.current / uploadStates.media.total) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadSuccess && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Files uploaded successfully!</span>
              </div>
            )}

            {/* Error Message */}
            {uploadError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700 font-medium">{uploadError}</span>
              </div>
            )}

            {/* Display uploaded media */}
            {/* Display uploaded media */}
            {media.length > 0 && (
              <div className="mb-3 space-y-2">
                {media.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0">
                      {item.type === 'external-video' ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getPlatformIcon(item.platform)}</span>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.platform.charAt(0).toUpperCase() + item.platform.slice(1)} Video
                            </p>
                          </div>
                          <a 
                            href={item.originalUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline truncate block"
                          >
                            {item.originalUrl}
                          </a>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 truncate">{item.fileName}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(item.fileSize)}</p>
                        </>
                      )}
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <button
                      onClick={() => removeMediaItem(index)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* File Upload Component - keep existing */}
            <FileUpload
              onFileSelect={handleMediaUpload}
              multiple={true}
              maxSize={25}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
              placeholder="Drag files here or click to upload"
              disabled={isUploading}
              loading={isUploading}
            />

            {/* ✅ ADD: External Video URL Input */}
            <div className="mt-3">
              {!showVideoInput ? (
                <button
                  onClick={() => setShowVideoInput(true)}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  <LinkIcon className="h-4 w-4" />
                  Add Video URL (YouTube, Loom, Vimeo, etc.)
                </button>
              ) : (
                <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Add External Video</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={externalVideoUrl}
                      onChange={(e) => {
                        setExternalVideoUrl(e.target.value);
                        setVideoUrlError('');
                      }}
                      placeholder="Paste YouTube, Loom, Vimeo, or Google Drive video URL..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAddExternalVideo}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowVideoInput(false);
                        setExternalVideoUrl('');
                        setVideoUrlError('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                  {videoUrlError && (
                    <p className="text-red-600 text-xs mt-2">{videoUrlError}</p>
                  )}
                  <p className="text-xs text-blue-700 mt-2">
                    Supported: YouTube, Loom, Vimeo, Google Drive, Dailymotion
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Tag className="h-4 w-4 inline mr-1" />
                    Tags
                  </label>
                  <input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="important, deadline, celebration"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>

                {/* Schedule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    Schedule (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
                <div className="flex gap-2">
                  {Object.entries(priorityOptions).map(([key, option]) => (
                    <button
                      key={key}
                      onClick={() => setPriority(key)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        priority === key ? option.color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {content.length} / 5000 characters
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={(!content.trim() && !title.trim()) || isUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {initialPost ? 'Update Post' : 'Publish Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};